define('uikit/mixins/injector.mixin',['require','exports','module','underscore','backbone','marionette','bluebird','../channel','../extra/rendering.manager','../extra/extend.util'],function(require, exports, module) {
  //prettier-ignore
  var _           = require('underscore'),
      Backbone    = require('backbone'),
      Mn          = require('marionette'),
      Promise     = require('bluebird'),
      channel     = require('../channel'),
      renderer    = require('../extra/rendering.manager'),
      ExtendUtil  = require('../extra/extend.util');

  // Make browser aware that we will be doing this.  This way we wont cause errors
  document.createElement('ui');

  // injector is specifically supposed to work with component.mixin
  // and a view that inherits off of Marionette.View

  // Injector mixin has a single purpose: Take the template of a view
  // and use it to auto-construct views
  var InjectorMixin = {
    componentSelector: 'ui',

    channel: channel,

    injectInternalViewsSynchronously: false,

    /**
     * Mapping of internal view names to their constructors
     */
    internalViews: null,

    getInternalViews: function() {
      return this._internalViews || [];
    },

    /**
     * Returns the internal view constructor for the given view name
     */
    getInternalView: function(name) {
      var internalView = Mn._getValue(this.getOption('internalViews'), this);
      return (internalView || {})[name];
    },

    internalViewOptions: null,

    /**
     * Returns the internal view constructor's options for the given view name
     */
    getInternalViewOptions: function(name) {
      var internalViewOptions = Mn._getValue(this.getOption('internalViewOptions'), this);
      return Mn._getValue((internalViewOptions || {})[name], this, [name]);
    },

    internalViewBindings: null,

    /**
     * Returns the internal view bindings for the given view name
     */
    getInternalViewBindings: function(name, childview) {
      var internalViewBindings = Mn._getValue(this.getOption('internalViewBindings'), this);

      return Mn._getValue((internalViewBindings || {})[name], this, [name, childview]);
    },

    initialize: function() {
      this._internalViews = (this._internalViews || []).slice();

      // if the internalViews def is not a function then copy
      // it onto the instance so it does not share the same object memory
      // space as the prototype
      if (!_.isFunction(this.internalViews)) {
        this.internalViews = _.extend({}, this.internalViews);
      }

      // Initally extract and setup components before render
      this._injectorInitialExtractComponents();

      this.listenToOnce(this, 'before:destroy', this._onInjectorBeforeDestroy);

      this.listenTo(this, 'before:render', this._onInjectorBeforeRender);
      this.listenTo(this, 'render', this._onInjectorRender);
      this.listenTo(this, 'show', this._onInjectorShow);
    },

    _onInjectorBeforeDestroy: function() {
      this._injectorDestroyInternalViews();
    },

    _onInjectorBeforeRender: function() {
      // Detach any internal views before rendering.  Rendering
      // will wipe out the html content via jquery, which
      // will kill all the internal views events.  if we
      // detach them, the events are saved, and when
      // they are reattached the events should still work
      if (this._isRendered) {
        _.each(
          this.getInternalViews(),
          function(viewName) {
            var view = this[viewName];

            if (view && view instanceof Backbone.View) {
              view.$el.detach();
            }
          },
          this
        );
      }
    },

    _onInjectorRender: function() {
      this._injectorExtractComponents(this.el);

      if (this._hasSetupComponent) {
        this._injectorUpdateInternalViews();
      }
    },

    _onInjectorShow: function() {
      _.each(
        this.getInternalViews(),
        function(viewName) {
          var view = this[viewName];

          if (view && view instanceof Backbone.View) {
            Mn.triggerMethodOn(view, 'show');
          }
        },
        this
      );
    },

    _injectorInitialExtractComponents: function() {
      var template = this.getTemplate();
      var badTags = ['img', 'embed', 'video', 'audio'];
      // Only proceed if a template exists
      if (template) {
        // Try to render html
        try {
          var div = document.createElement('div'),
            html = Mn.Renderer.render(template, this.serializeData(), this) || '';

          // clean out tags that might load things
          // we don't want....
          for (var i = 0, tag; i < badTags.length; i++) {
            tag = badTags[i];
            html = html.replace(new RegExp('<\\s*' + tag + ' ', 'g'), '<ignore ');
            html = html.replace(new RegExp('</\\s*' + tag + '\\s*>', 'g'), '</ignore>');
          }

          // TODO: don't actually create elements
          // when parsing out the UI tags

          // Insert html to gain reference
          div.innerHTML = html;

          this._injectorExtractComponents(div);
        } catch (e) {
          // Serialize data may depend on data not set until after initial render
          Promise.reject(e);
        }
      }
    },

    _injectorExtractComponents: function(el) {
      var elements = [];

      // Find all the component placeholders that need to be setup
      elements = el.getElementsByTagName(this.componentSelector);

      this._injectorSetupComponents(elements);
    },

    _injectorDestroyInternalViews: function() {
      _.each(
        this.getInternalViews(),
        function(viewName) {
          var view = this[viewName];

          if (view && view instanceof Mn.View) {
            view.destroy();
          }
        },
        this
      );
    },

    _injectorUpdateInternalViews: function() {
      var placeholders = [],
        placeholderEl = null,
        internalViews = this.getInternalViews();

      // Cache all the placeholders before inserting the element
      for (var i = 0, numOfInternalViews = internalViews.length; i < numOfInternalViews; i++) {
        // Find the placeholder using the internal view name
        placeholderEl = this.$(this.componentSelector + '[name="' + internalViews[i] + '"]');

        placeholders.push(placeholderEl);
      }

      for (var j = 0, numOfInternalViews = internalViews.length; j < numOfInternalViews; j++) {
        var view = this[internalViews[j]],
          didInject = false;

        if (!(view instanceof Backbone.View)) {
          continue;
        }

        (function(view, placeholderEl) {
          // Ensure the internal view has been rendered
          if (view._isRendered) {
            // Inject the internal view in its corresponding placeholder location
            this._injectInternalView(view, placeholderEl);
          } else if (this.getOption('injectInternalViewsSynchronously') === true) {
            view.render();
            // Inject the internal view in its corresponding placeholder location
            this._injectInternalView(view, placeholderEl);
          } else {
            renderer.requestRender(
              _.bind(function() {
                if (view.isDestroyed) {
                  return;
                }
                view.render();
                // Inject the internal view in its corresponding placeholder location
                this._injectInternalView(view, placeholderEl);
              }, this)
            );
          }
        }.call(this, view, placeholders[j]));
      }
    },

    _injectInternalView: function(view, placeholderEl) {
      if (this._isShown && view._shouldCallShow) {
        Mn.triggerMethodOn(view, 'before:show');
      }
      if (placeholderEl.length > 0) {
        placeholderEl.replaceWith(view.$el);
        if (this._isShown && view._shouldCallShow) {
          Mn.triggerMethodOn(view, 'show');

          delete view._shouldCallShow;
        }
        return true;
      } else {
        return false;
      }
    },

    _injectorSetupComponents: function(elements) {
      var length = elements.length,
        node = null,
        options = null,
        view = null;

      for (var n = 0; n < length; n++) {
        node = elements[n];
        // Extend the options found in the element with those declared on the view
        options = this._injectorExtractElementOptions(node);

        // Store internal view reference
        this._injectorStoreInternalViewReference(options.name);

        // Only proceed if allowed depending on options
        if (!this._injectorCanBuildComponent(options)) {
          continue;
        }

        /**
         * TODO - Avoid calling the internal view options twice
         *
         * Since we allow the ability to define an internal view after initialize,
         * we should check if the constructor is defined, so we can avoid retrieving the internal view options if unnecessary,
         * but since we know allow the ability to define the type in the internal view options, this is not possible
         *
         * we can't simply cache the results, since the internal view options may depend on options that are not yet defined until necessary
         */
        options = _.extend({}, options, this.getInternalViewOptions(options.name), {
          name: options.name,
          injectInternalViewsSynchronously: this.getOption('injectInternalViewsSynchronously'),
        });

        // Build the new component
        view = this._injectorBuildComponent(options);

        if (view && view instanceof Backbone.View) {
          this._injectorSetupInternalViewBindings(options.name, view);

          // Store a reference to the component on this view
          this._injectorStoreComponentReference(options.name, view);

          // Deconstruct component view if this view is destroyed
          this.once('destroy', view.destroy, view);
        }
      }
    },

    _injectorBuildComponent: function(options) {
      var Component = null;

      if (options.type) {
        Component = this.getComponentByType(options.type);

        // Only used to retrieve the correct component
        delete options.type;
      } else {
        Component = this.getInternalView(options.name);
      }

      if (Component && options.behaviors) {
        Component = ExtendUtil.extendClassBehaviors(Component, options);
      }

      if (Component) {
        options = this._injectorMapComponentOptions(options, Component);
        return new Component(options);
      }
    },

    _injectorMapComponentOptions: function(options, component) {
      var nameMap = { tagname: 'tagName', classname: 'className' },
        mapped = {},
        self = this;

      // Map each possible camelCase key to the lowercase key name
      _.each(_.result(component.prototype, 'options', {}), function(value, key) {
        nameMap[key.toLowerCase()] = key;
      });

      _.each(options, function(value, key) {
        mapped[nameMap[key] || key] = self.parseAttributeValue(value, key);
      });

      return mapped;
    },

    _injectorExtractElementOptions: function(node) {
      var attributes = node.attributes,
        len = attributes.length,
        attr = null,
        attrName = null,
        options = {};

      for (var a = 0; a < len; a++) {
        attr = node.attributes.item(a);
        attrName = attr.name.toLowerCase();

        options[attrName] = this.parseAttributeValue(attr.value, attrName);
      }

      return options;
    },

    _injectorCanBuildComponent: function(options) {
      var view = this[options.name];

      return options.name && !(view && view instanceof Backbone.View);
    },

    _injectorStoreComponentReference: function(name, view) {
      this[name] = view;
      this.triggerMethod('injected', view);
      this.triggerMethod('injected:' + name, view);
    },

    _injectorStoreInternalViewReference: function(name) {
      var internalViews = this._internalViews;

      if (name && internalViews.indexOf(name) == -1) {
        this._internalViews = internalViews.slice();
        this._internalViews.push(name);

        // this is defined in component.mixin.js
        this._initializeProperty(name, 'options');
      }
    },

    // Parse the value of an attribute
    parseAttributeValue: function(value, name) {
      if (value === 'true') {
        value = true;
      } else if (value === 'false') {
        value = false;
      }
      return value;
    },

    getComponentByType: function(type) {
      if (_.isFunction(type)) {
        return type;
      }
      return this.channel.request(String(type || ''));
    },

    /** Internal view bindings **/

    _injectorSetupInternalViewBindings: function(name, childview) {
      var bindings = this.getInternalViewBindings(name, childview);

      if (bindings) {
        this._injectorBindInternalView(childview, bindings);
      }

      return this;
    },

    /**
     * For the specified internal view, set any bindings based on the arguments
     */
    _injectorBindInternalView: function(childView, bindings) {
      var ev = _.extend({}, Backbone.Events),
        self = this;

      // Unbind any possibly existing events
      this._injectorUnbindInternalView(childView);

      // Stop listening when any of the views are destroyed
      ev
        .listenTo(this, 'before:destroy', function() {
          ev.stopListening();
        })
        .listenTo(childView, 'before:destroy', function() {
          ev.stopListening();
        });

      // Listen to each view for changes
      _.each(bindings, function(viewField, cvField) {
        // Set initial values
        childView[cvField] = self.getOption(viewField);

        ev
          .listenTo(self, 'change:' + viewField, function(self, value) {
            childView[cvField] = value;
          })
          .listenTo(childView, 'change:' + cvField, function(childView, value) {
            self[viewField] = value;
          });
      });

      // Cache event listeners
      this._injectorCacheInternalViewBinding(childView, ev);
    },

    /**
     * Unbind any existing event listeners for the same internal view
     */
    _injectorUnbindInternalView: function(cv) {
      var existing = this._internalViewBindings || {},
        oldEvent = existing[cv.cid];

      if (oldEvent) {
        oldEvent.stopListening();
      }
    },

    /**
     * Cache the new event listeners using the childview unique id
     */
    _injectorCacheInternalViewBinding: function(cv, ev) {
      this._internalViewBindings = this._internalViewBindings || {};

      this._internalViewBindings[cv.cid] = ev;
    },
  };

  module.exports = InjectorMixin;
});

